import React, {
  useState, useEffect, useCallback, forwardRef,
} from 'react';
import { uniqueId, omit, isNil } from 'lodash';
// components
import BaseGrid from '../BaseGrid';
import { useToast } from 'shared/toast';
import { AddButton, RecycleBinButton } from 'shared/buttons';
import GridInlineFormRow from './GridInlineFormRow';
import FormActionsCell from './FormActionsCell';
import { emptyObject } from 'src/shared/helpers/utils';

// This is a unique key property for each form data item given
// for new creation internally to handle new but not added items
const FORM_DATA_INDEX = 'formDataIndex';
// This is a unique key property for external form data item given
// for local creation that exist past the discard lifecycle
// that above FORM_DATA_INDEX does not
// and is need before the DATA_ITEM_KEY is created by db
export const FORM_GRID_LOCAL_KEY = 'key';

const DATA_ITEM_KEY = 'id';
const IN_EDIT_KEY = 'isInEdit';
const IN_DELETE_KEY = 'isInDelete';
const GROUP_HEADER_KEY = 'groupHeader';

/**
 * @typedef {Object} FormGridProps
 * @property {boolean} hideBaseAddButton - Needed when used inside DataGrid Determines since a hideAddButton would also hide the add button for the base grid.
 */

const FormGrid = forwardRef(({
  id,
  data,
  itemName = 'this item',
  initialRowValue,
  identifierColumn = DATA_ITEM_KEY,
  localIdentifierColumn = FORM_GRID_LOCAL_KEY,
  inEditKey = IN_EDIT_KEY,
  inDeleteKey = IN_DELETE_KEY,
  allowMultiEdit = true,
  allowMultiAdd = false,
  allowNextAdd = false, // add on every line
  enableReinitialize = true,
  confirmDelete = true,
  hideAddButton = false,
  hideDeleteButton = false,
  // Needed due to prop collisions
  hideBaseAddButton = false,
  hideBaseRecycleButton = false,
  hideEditButton = false,
  hideActionBar = false,
  hideCancelButton = false,
  onRowSubmit,
  onSave, // override using onSave in FormActionsCell from cell submit (which does not work in nested arrays)
  useHandleRowSubmit = false, // overide using onSave in FormActionsCell from cell submit (which does not work in nested arrays)
  onEnterEdit,
  onExitEdit,
  onNewRowSubmit, // if it's different from onRowSubmit
  onRowDelete,
  onEnterHistory,
  onAllDelete,
  onAllSubmit,
  validationSchema,
  validationContext,
  actionBarContent,
  columns,
  defaultInEdit = false,
  forceReload = false,
  didLoad,
  onNextAdd,
  inRowEdit = true,
  // action cell
  hideActionsCell,
  additionalActions,
  additionalActionsPlacement,
  actionCellHiddenCallback = null,
  actionCellWidth = null,
  disabled,
  didChange,
  onDiscard = null,
  readOnly,
  // toast
  notify = true,
  resetEditStateOnDataChange = true,
  ...props
}, ref) => {
  const toast = useToast();
  // inner data state
  const buildInnerData = (items) => items.map((dataItem) => ({ ...dataItem, [FORM_DATA_INDEX]: uniqueId() }));

  const [innerData, setInnerData] = useState(buildInnerData(data));
  const setData = () => {
    setInnerData((prev) => data.map((d) => {
      const identifier = d[identifierColumn] ?? d[localIdentifierColumn];
      // handle updates with multiple undefined IDs so first is not auto selected
      const found = identifier && prev.find((p) => p[identifierColumn] === identifier);
      return found ? { ...found, ...d } : { ...d, [FORM_DATA_INDEX]: uniqueId() };
    }));
  };

  // force reload
  useEffect(() => {
    if (forceReload) {
      setData();
      if (didLoad) {
        didLoad();
      }
    }
  }, [forceReload]);

  // items in edit (Not indexes, but unique keys from FORM_DATA_INDEX. Name is missleading)
  const [editIndexes, setEditIndexes] = useState([]);

  useEffect(() => {
    setInnerData((prev) => prev.map(
      (item) => ({
        ...item,
        [inEditKey]: defaultInEdit || editIndexes.includes(item[FORM_DATA_INDEX]),
      }),
    ));
  }, [editIndexes]);

  const enterEdit = (dataItem) => {
    if (!inRowEdit) {
      return;
    }

    if (!allowMultiEdit) {
      setEditIndexes([dataItem[FORM_DATA_INDEX]]);
    } else {
      setEditIndexes((prev) => [...prev, dataItem[FORM_DATA_INDEX]]);
    }
  };

  // update data
  useEffect(() => {
    if (enableReinitialize) {
      setData();

      if (resetEditStateOnDataChange) {
        setEditIndexes([]);
      }

      if (didLoad) {
        didLoad();
      }
    }
  }, [data]);

  // form handlers
  const getValue = (dataItem) => omit(dataItem, [FORM_DATA_INDEX, inEditKey, 'expanded']);
  // submit
  const handleRowSubmit = useCallback((dataItem, _, __, onFinish) => {
    const value = getValue(dataItem);

    const onSuccess = (response = {}) => {
      if (didChange) {
        didChange();
      } else {
        const index = innerData.findIndex((i) => i[FORM_DATA_INDEX] === dataItem[FORM_DATA_INDEX]);
        const newDataState = [...innerData];
        newDataState[index] = { ...dataItem, ...response, [inEditKey]: false };
        setInnerData(newDataState);
        setEditIndexes((prev) => prev.filter((ind) => ind !== dataItem[FORM_DATA_INDEX]));
      }
      if (notify) {
        toast.success({ message: 'Saved Successfully' });
      }
    };

    const onError = (error) => {
      // TODO: Below line was causing to data loss for new rows on request fails.
      // Keeping it commented for revert if we really need it in future.
      // setInnerData((old) => old.map((d) => ({ ...d, isSubmitting: false })));
      if (notify) {
        toast.error({ message: 'Failed to save', error });
      }
    };

    if (onNewRowSubmit && isNil(dataItem[identifierColumn])) {
      onNewRowSubmit(value, onSuccess, onError, onFinish);
    } else if (onRowSubmit) {
      onRowSubmit(value, onSuccess, onError, onFinish);
    } else {
      onSuccess();
    }
  }, [innerData]);

  // delete
  const setItemInDelete = (dataItem, value) => {
    setInnerData((prev) => {
      const index = prev.findIndex((p) => p[FORM_DATA_INDEX] === dataItem[FORM_DATA_INDEX]);
      const newDataState = [...prev];
      newDataState[index] = { ...dataItem, [inDeleteKey]: value };
      return newDataState;
    });
  };

  const handleRowDelete = useCallback((dataItem, cellProps) => {
    setItemInDelete(dataItem, true);
    const onSuccess = () => {
      if (didChange) {
        didChange();
      } else {
        setInnerData((prev) => prev.filter((p) => p[FORM_DATA_INDEX] !== dataItem[FORM_DATA_INDEX]));
      }
      if (notify) {
        toast.success({ message: 'Deleted successfully' });
      }
    };
    const onError = (error) => {
      if (notify) {
        toast.error({ message: 'Failed to delete', error });
      }
    };
    const onFinish = () => setItemInDelete(dataItem, false);

    if (onRowDelete) {
      // Wrap the onRowDelete callback to handle cancellation errors
      const wrappedOnError = (error) => {
        // Check if this is a cancellation error
        if (error?.message?.toLowerCase()?.includes('cancelled by user')) {
          // If user cancelled, just call onFinish to reset the loading state
          onFinish();
          return;
        }
        // For other errors, call the original onError
        onError(error);
      };
      
      onRowDelete(getValue(dataItem), onSuccess, wrappedOnError, onFinish, cellProps);
    } else {
      onSuccess();
      onFinish();
    }
  }, [innerData]);

  // add
  const hasNew = innerData.find((d) => d[IN_EDIT_KEY]);
  const onAddRowClick = useCallback(() => {
    if (onEnterEdit) {
      onEnterEdit?.(null, 'new');
    }
    if (hasNew && !allowMultiAdd) {
      onEnterEdit?.(null, 'new');
      return;
    }
    const newItem = { ...initialRowValue, [FORM_DATA_INDEX]: uniqueId() };
    const newDataState = [newItem, ...innerData];
    setInnerData(newDataState);
    onEnterEdit?.(newItem, 'new');
    enterEdit?.(newItem);
  }, [innerData]);

  // discard
  const handleRowDiscard = useCallback((dataItem) => {
    if (onExitEdit) {
      onExitEdit();
    }

    setEditIndexes((prev) => prev.filter((ind) => ind !== dataItem[FORM_DATA_INDEX]));
    if (isNil(dataItem[identifierColumn]) && isNil(dataItem[localIdentifierColumn])) {
      setInnerData((prev) => prev.filter((p) => p[FORM_DATA_INDEX] !== dataItem[FORM_DATA_INDEX]));
    }

    if (onDiscard) {
      onDiscard(dataItem);
    }
  }, [innerData]);

  const handleNextAdd = useCallback((dataItem) => {
    const currData = innerData;
    const newItem = {
      ...initialRowValue,
      [FORM_DATA_INDEX]: uniqueId(),
      [DATA_ITEM_KEY]: Math.max(...currData.map((o) => o.id)) + 1,
    };
    const index = currData.findIndex((c) => c[DATA_ITEM_KEY] === dataItem[DATA_ITEM_KEY]);
    currData.splice(index + 1, 0, newItem);
    setInnerData(currData);
    enterEdit(newItem);
    if (onNextAdd) {
      onNextAdd();
    }
  }, [innerData]);

  const handleEnterEdit = (dataItem) => {
    if (onEnterEdit) {
      onEnterEdit(dataItem, 'edit');
    }

    enterEdit?.(dataItem);
  };

  const isEditing = innerData.some((d) => d[inEditKey]);

  // add actions to the columns
  // const buildColumns = () => [
  //   ...(columns ?? []),
  //   {
  //     title: 'Actions',
  //     isSystem: true,
  //     sortable: false,
  //     width: actionCellWidth ?? 175,
  //     cell: ({ dataItem, rowType, ...cellProps }) => (
  //       <FormActionsCell
  //         isEditing={isEditing}
  //         onDelete={!hideDeleteButton && rowType !== GROUP_HEADER_KEY ? () => handleRowDelete?.(dataItem, cellProps) : undefined}
  //         onEnterHistory={onEnterHistory && rowType !== GROUP_HEADER_KEY ? () => onEnterHistory(dataItem) : undefined}
  //         onEnterEdit={!hideEditButton && rowType !== GROUP_HEADER_KEY ? () => handleEnterEdit?.(dataItem) : undefined}
  //         onExitEdit={onExitEdit}
  //         onDiscard={!hideCancelButton && rowType !== GROUP_HEADER_KEY ? () => handleRowDiscard?.(dataItem) : undefined}
  //         dataItem={dataItem}
  //         editField={inEditKey}
  //         deleteField={inDeleteKey}
  //         formDataIndex={FORM_DATA_INDEX}
  //         defaultInEdit={defaultInEdit}
  //         itemName={itemName}
  //         disabled={disabled}
  //         hidden={actionCellHiddenCallback && actionCellHiddenCallback(dataItem)}
  //         confirmDelete={confirmDelete}
  //         additionalActions={additionalActions}
  //         additionalActionsPlacement={additionalActionsPlacement}
  //         hideEditButton={hideEditButton}
  //         {...cellProps}
  //         id={`${id ?? ''}${id && dataItem[identifierColumn] ? '-' : ''}${dataItem[identifierColumn] ?? ''}`}
  //         useHandleRowSubmit={useHandleRowSubmit}
  //         handleRowSubmit={handleRowSubmit}
  //       />
  //     ),
  //     hide: hideActionsCell || readOnly,
  //   },
  // ];

  const getActionCellProps = React.useCallback((localProps) => {
    const { dataItem, rowType, ...cellProps } = localProps;
    return ({
      ...props.actionCellProps,
      isEditing,
      onDelete: !hideDeleteButton && rowType !== GROUP_HEADER_KEY ? () => handleRowDelete?.(dataItem, cellProps) : undefined,
      onEnterHistory: onEnterHistory && rowType !== GROUP_HEADER_KEY ? () => onEnterHistory(dataItem) : undefined,
      onEnterEdit: !hideEditButton && rowType !== GROUP_HEADER_KEY ? () => handleEnterEdit?.(dataItem) : undefined,
      onExitEdit,
      onDiscard: !hideCancelButton && rowType !== GROUP_HEADER_KEY ? () => handleRowDiscard?.(dataItem) : undefined,
      dataItem,
      editField: inEditKey,
      deleteField: inDeleteKey,
      formDataIndex: FORM_DATA_INDEX,
      defaultInEdit,
      itemName,
      disabled,
      hidden: actionCellHiddenCallback && actionCellHiddenCallback(dataItem),
      confirmDelete,
      additionalActions,
      additionalActionsPlacement,
      hideEditButton,
      ...cellProps,
      id: `${id ?? ''}${id && dataItem[identifierColumn] ? '-' : ''}${dataItem[identifierColumn] ?? ''}`,
      useHandleRowSubmit,
      handleRowSubmit,
      ...props.getActionCellProps?.(localProps) ?? emptyObject,
    });
  }, [isEditing, hideDeleteButton, handleRowDelete, onEnterHistory, hideEditButton, handleEnterEdit, hideCancelButton, handleRowDiscard, inEditKey, inDeleteKey, FORM_DATA_INDEX, defaultInEdit, itemName, disabled, actionCellHiddenCallback, confirmDelete, additionalActions, additionalActionsPlacement, id, identifierColumn, useHandleRowSubmit, handleRowSubmit]);

  // const innerColumns = buildColumns();

  // form row
  const rowRender = (row, { dataItem }) => (
    <GridInlineFormRow
      dataItem={dataItem}
      isInEdit={dataItem[inEditKey]}
      formDataIndex={FORM_DATA_INDEX}
      validationSchema={validationSchema}
      validationContext={validationContext}
      onSubmit={handleRowSubmit}
    >
      {row}
    </GridInlineFormRow>
  );

  return (
    <BaseGrid
      itemName={itemName}
      {...props}
      ActionsCell={FormActionsCell}
      getActionCellProps={getActionCellProps}
      onRowSubmit={handleRowSubmit}
      ref={ref}
      data={innerData}
      columns={columns ?? []}
      hideActionsCell={hideActionsCell}
      rowRender={rowRender}
      identifierColumn={identifierColumn}
      actionBarContent={hideActionBar ? null : (
        <>
          {(!hideAddButton && !hideBaseAddButton && !readOnly) && (<AddButton disabled={disabled || (hasNew && !allowMultiAdd)} onClick={onAddRowClick} />)}
          {props.hasRecycleBin && !hideBaseRecycleButton && !readOnly && (
            <RecycleBinButton
              value={props.recycleBin}
              onShow={() => {
                props.showRecycleBin?.();
                props.onShowRecycleBin?.();
              }}
              onHide={() => {
                props.hideRecycleBin?.();
                props.onHideRecycleBin?.();
              }}
            />
          )}
          {actionBarContent}
        </>
      )}
    />
  );
});

export default FormGrid;